import Filter from '@/src/components/organism/Filter';
import { useEffect, useRef, useState } from 'react';
import { communityReportFilters } from './filter';
import { useQuery } from '@tanstack/react-query';
import useQueryValues from '@/src/hooks/useQueryValues';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { AgGridReact } from 'ag-grid-react';
import Table from '@/src/components/atom/Table';
import { communityReportListColumn } from './column';
import SectionCardNoneTitle from '@/src/components/molecule/SectionCardNoneTitle';
import Typo from '@/src/components/atom/Typo';
import usePaginationQuery from '@/src/hooks/usePaginationQuery';
import { NAV_CONST } from '@/src/constants/nav';
import {
  getCommunityReportsAPI,
  getCommunityReportByIdAPI,
  getCommunityReportExcelAPI,
  getCommunityReportReasonsAPI,
} from '@/src/apis/rest/communityReport';
import CommunityReportBlindModal from '@/src/pages/CommunityReport/components/CommunityReportBlindModal';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';
import PATH from '@/src/routes/path';
import { useNavigate } from 'react-router-dom';

type ModalState = 'NONE' | 'COMMUNITY_BLIND' | 'COMMENT_DETAIL';

export default function ComunityReportListPage() {
  const { defaultValues } = useQueryValues({
    createdAt: ['createdAtFrom', 'createdAtTo'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();
  const navigate = useNavigate();

  const gridRef = useRef<AgGridReact>(null);
  const [values, setValues] = useState(defaultValues);
  const [modalState, setModalState] = useState<ModalState>('NONE');
  const [selectedDetailId, setSelectedDetailId] = useState<number | null>(null);

  const {
    data: tableData,
    isError,
    isFetching,
    isFetched,
    refetch: refetchTableData,
  } = useQuery({
    queryKey: ['community-posting', values, page, pageSize],
    queryFn: async () => {
      return getCommunityReportsAPI({ ...values, page, pageSize });
    },
  });

  const { data: communityReportReasonOptions } = useQuery({
    queryKey: ['community-report-reason-options'],
    queryFn: getCommunityReportReasonsAPI,
    select: data => [
      { label: '전체', value: '' },
      ...data.map(({ key, value }) => ({
        label: value,
        value: key,
      })),
    ],
  });

  const { data: reportDetailData } = useQuery({
    queryKey: ['community-posting-detail', selectedDetailId],
    queryFn: () => {
      return getCommunityReportByIdAPI({ id: Number(selectedDetailId) });
    },
    enabled: !!selectedDetailId,
  });

  // event
  const handleCloseModal = () => {
    setSelectedDetailId(null);
    setModalState('NONE');
  };
  const handleToggleModal = (type: ModalState) => {
    if (modalState === type) {
      handleCloseModal();
    } else {
      setModalState(type);
    }
  };

  // effect
  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  return (
    <>
      <MainContentTitle
        pageTitle={NAV_CONST.communityReportManagement}
        breadcrumbList={[NAV_CONST.communityReportManagement]}
      />
      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={communityReportFilters(communityReportReasonOptions || [])}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setValues(values);
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">신고 접수 목록</Typo>
              <div className="flex gap-2">
                <ExcelDownloadButton
                  disabled={isError || !isFetched || tableData?.count === 0}
                  fileName="신고_접수_리스트"
                  searchParams={values}
                  downloadExcelAPI={getCommunityReportExcelAPI}
                />
              </div>
            </div>
            <Table
              ref={gridRef}
              rowData={isError ? [] : tableData?.rows || []}
              isPaginationDataMapping
              columnDefs={communityReportListColumn(e => {
                navigate(
                  PATH.COMMUNITY_REPORT_DETAIL.replace(':id', String(e)),
                );
              })}
              total={tableData?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>
      {/* 모달 */}
      {reportDetailData && modalState === 'COMMUNITY_BLIND' && (
        <CommunityReportBlindModal
          open={modalState === 'COMMUNITY_BLIND'}
          onOpenChange={() => handleToggleModal('COMMUNITY_BLIND')}
          onSubmit={refetchTableData}
          data={reportDetailData}
        />
      )}
    </>
  );
}
